import { useForm, yupResolver } from "@mantine/form"
import { useLocalStorage } from "@mantine/hooks"
import { CheckCircle, Trash } from "@phosphor-icons/react"
import {
  Breadcrumbs,
  Button,
  Checkbox,
  FormInput,
  FormQuerySelector,
  Group,
  InputGroup,
  SpinnerOverlay,
  Tag,
  Text,
  Toast,
} from "@vesatogo/grass-core"
import { FormDateInput, FormTimeInput } from "@vesatogo/grass-dates"
import "@vesatogo/grass-dates/index.css"
import { inr } from "@vesatogo/utils"
import classNames from "classnames"
import dayjs from "dayjs"
import { cloneDeep, debounce, get, isEmpty, set } from "lodash-es"
import { useEffect, useMemo, useRef, useState } from "react"
import toast from "react-hot-toast"
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom"
import AttachmentModal from "~/components/AttachmentModal"
import Attachments from "~/components/Attachments"
import { FormCard } from "~/components/FormCard"
import HowIsItTraded from "~/components/HowIsItTraded"
import TermsAndConditions from "~/components/TermsAndConditions"
import TradeExpenses from "~/components/TradeExpenses"
import WhatIsBeingTraded from "~/components/WhatIsBeingTraded"
import { WhoIsTrading } from "~/components/WhoIsTrading"
import WhoOverseesTrading from "~/components/WhoOverseesTrading"
import Codenames from "~/constants/Codenames"
import { TradeKinds } from "~/constants/Kinds"
import SharedEntityGroup from "~/constants/SharedEntityGroup"
import { AppRoutes } from "~/constants/routes"
import {
  useAddUpdateSharedEntityMutation,
  useAllDepartmentsQuery,
  useAllPackagingCommoditiesQuery,
  useAllSiUnitsQuery,
  useAllVarietiesQuery,
  useAllVarietyGradesQuery,
  useDeleteExpenseItemMutation,
  useDemandDetailQuery,
  useDemandEnquiriesQuery,
  useGetSharedEntityQuery,
  useGetVarietyGradesSharedEntityQuery,
  useIncrementTradeCounterMutation,
  useOfferDetailQuery,
  useOfferEnquiriesQuery,
  useRemainingStockQuantityQuery,
  useStockPattiListQuery,
  useUnitSiUnitsQuery,
  useUpsertDemandMutation,
  useUpsertOfferMutationMutation,
  useVarietyGradeDetailQuery,
} from "~/generated/graphql"
import { getPattiValidationSchema } from "~/models/patti"
import { SideBar } from "~/pages/d/trade-requests/sections/SideBar"
import {
  expenseAmount,
  expenseAmountBuyer,
  normalizeExpenses,
} from "~/utils/expenseAmount"
import { useDepartment } from "../../../utils/useDepartment"

type SharedEntity = {
  group: SharedEntityGroup
}

const PattiDetail = () => {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const type = searchParams.get("type")
  const isDemandPatti = type === "demand"
  const isOfferPatti = type === "offer"
  const fetchingGradeRef = useRef(new Set<number>())

  const materialKind = searchParams.get("material_kind")
  const commodityId = searchParams.get("commodityId")
  const varietyId = searchParams.get("varietyId")
  const gradeId = searchParams.get("gradeId")
  const { id } = useParams()
  const idNum = parseInt(id as any)

  const [attachmentModal, setAttachmentModal] = useState(false)
  const [, upsertSharedEntity] = useAddUpdateSharedEntityMutation()
  const [{ fetching: mutationFetching, data: uData }, upsertDemand] =
    useUpsertDemandMutation()

  const [, upsertOffer] = useUpsertOfferMutationMutation()

  // const [, deleteTradeItem] = useDeleteTradeItemMutation()
  const [, deleteExpenseItem] = useDeleteExpenseItemMutation()
  const [, incrementTradeCounter] = useIncrementTradeCounterMutation()
  const { id: sharedDeptId } = useDepartment()
  const [dept] = useLocalStorage({ key: "department" })

  const [{ data: alldepts }] = useAllDepartmentsQuery()

  const currentDept = alldepts?.department?.find(
    dep => dep?.id?.toString() === dept?.toString()
  )

  const [{ data: deptData }] = useGetSharedEntityQuery({
    variables: {
      group: SharedEntityGroup.KiteDepartment,
      codename: currentDept?.id,
    },
    pause: !currentDept?.id,
  })

  const [{ data: sData }] = useGetSharedEntityQuery({
    variables: {
      group: SharedEntityGroup.KiteCurrency,
      codename: Codenames.KiteDefaultCurrency,
    },
  })

  const [{ data: demandDetail, fetching: demandFetching }] =
    useDemandDetailQuery({
      variables: {
        id: idNum,
      },
      pause: !idNum || !isDemandPatti,
      requestPolicy: "network-only",
    })

  const [{ data: offerDetail, fetching: offerFetching }] = useOfferDetailQuery({
    variables: {
      id: idNum,
    },
    pause: !idNum || !isOfferPatti,
    requestPolicy: "network-only",
  })

  const [{ data: demandEnquiries, fetching: demandEnquiriesFetching }] =
    useDemandEnquiriesQuery({
      variables: {
        where: {
          demand_id: {
            _eq: idNum,
          },
        },
      },
      pause: !idNum || !demandDetail || !isDemandPatti,
      requestPolicy: "network-only",
    })

  const [{ data: offerEnquiries, fetching: offerEnquiriesFetching }] =
    useOfferEnquiriesQuery({
      variables: {
        where: {
          offer_id: {
            _eq: idNum,
          },
        },
      },
      pause: !idNum || !offerDetail || !isOfferPatti,
      requestPolicy: "network-only",
    })

  const [{ data: gradeSharedEntity }] = useGetVarietyGradesSharedEntityQuery({
    variables: {
      where: {
        id: {
          _eq: gradeId,
        },
      },
    },
    pause: !gradeId,
  })

  const [{ data: gradeDetail, fetching: gradeDetailFetching }] =
    useVarietyGradeDetailQuery({
      variables: {
        id: Number(
          gradeSharedEntity?.setu_trade_shared_entity?.[0]?.variety_grade?.[0]
            ?.id
        ),
      },
      pause: !gradeId || !gradeSharedEntity,
    })
  const [{ data: remainingQuantity, fetching: remainingQuantityFetching }] =
    useRemainingStockQuantityQuery({
      variables: {
        where: {
          trade_items: {
            variety_grade_id: {
              _eq: gradeId,
            },
          },
          kind: {
            _eq: TradeKinds.StoragePatti,
          },
        },
      },
      pause: !gradeId,
    })

  const [{ data: storagePattis, fetching: storagePattisFetching }] =
    useStockPattiListQuery({
      variables: {
        filter: {
          grade_id: gradeId,
        },
        limit: 1,
      },
      pause: !gradeId,
    })

  const [{ data: UnitSI }] = useUnitSiUnitsQuery()

  const maxQuantity =
    remainingQuantity?.trade_aggregate?.aggregate?.sum
      ?.deducted_quantity_remaining || 0

  const isPackaging = materialKind === "packaging"
  //  ||
  // dData?.trade_by_pk?.material_kind === "packaging"

  const defaultCurrency = sData?.setu_trade_shared_entity?.[0]
  const form = useForm<any>({
    initialValues: {
      mode: "direct",
      kind: "pro_forma",
      material_kind: materialKind,
      attachments: [] as any,
      packaging: "raw_material",
      trade_items: [{ rate: 0 }],
      start_date: new Date(),
      end_date: null,
      expiry: null,
      expenses: [{ buyer_expense: {} }],
      advance_payment: [],
      credit_notes: [],
      tracking_code: crypto.randomUUID(),
    },
    validate: yupResolver(getPattiValidationSchema(isDemandPatti)),
  })

  const { values: state, setValues: setState } = form

  const [removedTradeItems, setRemovedTradeItems] = useState<
    Record<string, any>
  >({})
  const [removedExpenseItems, setRemovedExpenseItems] = useState<
    Record<string, any>
  >({})

  useEffect(() => {
    if (uData?.add_update_demand) {
      setRemovedTradeItems({})
      setRemovedExpenseItems({})
      // updateTradeState(uData?.demand)
    }
  }, [uData])

  useEffect(() => {
    if (
      isPackaging &&
      UnitSI?.si_unit?.[0]
      // &&
      // (stateRef.current?.quantity_unit?.shared_id === undefined ||
      //   stateRef.current?.quantity_unit?.codename !==
      //     UnitSI?.si_unit?.[0]?.codename)
    ) {
      const unit = UnitSI.si_unit[0]
      upsertSharedEntity({
        reference_id: unit.codename,
        group_name: SharedEntityGroup.KiteSiUnit,
        name: unit.name,
      }).then(({ data }) => {
        setState(prev => ({
          ...prev,
          quantity_unit: {
            ...unit,
            shared_id: data?.insert_setu_trade_shared_entity_one?.id,
          },
        }))
      })
    }
  }, [isPackaging, UnitSI])

  const runComputations = useMemo(
    () =>
      debounce(values => {
        const stateCopy = cloneDeep(values)
        const trade = stateCopy
        let tradeTotalQty = 0,
          tradeTotalAmount = 0,
          totalExpenseAmount = 0,
          buyerExpenseAmount = 0,
          advanceTotalAmount = 0
        for (let j = 0; j < trade.trade_items.length; j++) {
          let itemTotalAmount = 0
          const item = trade.trade_items[j]
          const quantity = Number(item.quantity) || 0
          const rate = Number(item.max_rate) || Number(item.rate) || 0
          itemTotalAmount += quantity * rate
          stateCopy.trade_items[j].total_amount = itemTotalAmount
          tradeTotalAmount += itemTotalAmount
          tradeTotalQty += quantity
        }
        stateCopy.total_quantity = tradeTotalQty
        stateCopy.total_amount = tradeTotalAmount
        // receiptTotalQty += tradeTotalQty
        // receiptTotalAmount += tradeTotalAmount

        stateCopy.amount_deducted =
          stateCopy.total_amount *
          (Number(stateCopy.standard_deduction || 0) / 100)

        stateCopy.quantity_deducted =
          stateCopy.total_quantity *
          (Number(stateCopy.standard_deduction || 0) / 100)

        for (let j = 0; j < stateCopy.expenses.length; j++) {
          const expense = stateCopy.expenses[j]
          const expenseParams = {
            kind: expense?.kind?.id,
            buyerAmount: Number(expense.buyer?.value) || 0,
            sellerAmount: Number(expense.seller?.value) || 0,
            facilitatorAmount: Number(expense.facilitator?.value) || 0,
            finalAmount: stateCopy.total_amount,
            finalQuantity:
              stateCopy.total_quantity - stateCopy.quantity_deducted,
            totalQuantity: stateCopy.total_quantity,
          }
          expense.amount = expenseAmount(expenseParams)
          totalExpenseAmount += expense.amount
          expense.buyer_expense = expenseAmountBuyer(expenseParams)
          buyerExpenseAmount += expense.buyer_expense
        }

        for (let j = 0; j < stateCopy.advance_payment.length; j++) {
          const advance = stateCopy.advance_payment[j]
          advanceTotalAmount += Number(advance.amount) || 0
        }
        stateCopy.advance_amount = advanceTotalAmount
        stateCopy.expense_amount = totalExpenseAmount
        stateCopy.buyer_expense = buyerExpenseAmount
        stateCopy.buyer_amount =
          stateCopy.total_amount -
          stateCopy.amount_deducted +
          stateCopy.buyer_expense

        setState(prev => ({
          ...stateCopy,
          quantity_unit: prev.quantity_unit?.shared_id
            ? prev.quantity_unit
            : stateCopy.quantity_unit,
        }))
      }, 500),

    []
  )

  useEffect(() => {
    const patti = isDemandPatti
      ? demandDetail?.demand_by_pk
      : offerDetail?.offers_by_pk
    const enquiries = isDemandPatti
      ? demandEnquiries?.demand_enquiries
      : offerEnquiries?.offer_enquiries
    const tradeItems = [
      {
        rate: patti?.rate || 0,
        quantity: patti?.quantity || 0,
        max_rate: patti?.max_rate,
        total_amount: patti?.total_amount,
        variety_grade: idNum
          ? {
              ...patti?.variety_grade?.variety_grade[0],
              shared_id: patti?.variety_grade?.id,
            }
          : {
              id: gradeDetail?.variety_grade_by_pk?.id,
              name: gradeDetail?.variety_grade_by_pk?.name,
              codename: gradeDetail?.variety_grade_by_pk?.codename,
              shared_id: gradeId,
            },
      },
    ]

    if ((patti && enquiries) || gradeDetail) {
      const newValues = {
        ...state,
        ...patti,
        expenses: normalizeExpenses(patti?.expenses || []),
        officer: patti?.sr_officer?.user[0]
          ? { ...patti?.sr_officer?.user[0], shared_id: patti.sr_officer?.id }
          : undefined,
        junior_officer: patti?.jr_officer?.user[0]
          ? {
              ...patti.jr_officer?.user[0],
              shared_id: patti.jr_officer?.id,
            }
          : undefined,

        quantity_unit: patti?.quantity_unit?.si_unit?.[0]
          ? {
              ...patti?.quantity_unit?.si_unit?.[0],
              shared_id: patti?.quantity_unit?.id,
            }
          : isPackaging
          ? UnitSI?.si_unit?.[0]
          : undefined,
        is_rate_range: patti?.max_rate,
        commodity: !idNum
          ? {
              id: gradeDetail?.variety_grade_by_pk?.variety?.commodity?.id,
              name: gradeDetail?.variety_grade_by_pk?.variety?.commodity?.name,
              codename:
                gradeDetail?.variety_grade_by_pk?.variety?.commodity?.codename,
              shared_id: commodityId,
            }
          : {
              ...patti?.commodity?.commodity[0],
              shared_id: patti?.commodity?.id,
            },
        commodity_variety: !idNum
          ? {
              id: gradeDetail?.variety_grade_by_pk?.variety?.id,
              name: gradeDetail?.variety_grade_by_pk?.variety?.name,
              codename: gradeDetail?.variety_grade_by_pk?.variety?.codename,
              shared_id: varietyId,
            }
          : {
              ...patti?.commodity_variety?.commodity_variety[0],
              shared_id: patti?.commodity_variety?.id,
            },
        trade_items: tradeItems,

        seller: isDemandPatti
          ? enquiries?.map(enquiry => ({
              ...enquiry.seller?.user?.[0],
              shared_id: enquiry.seller?.id,
              enquiry_id: enquiry.id,
            }))
          : offerDetail?.offers_by_pk?.seller
          ? {
              ...offerDetail?.offers_by_pk?.seller?.user?.[0],
              shared_id: offerDetail?.offers_by_pk?.seller?.id,
            }
          : undefined,
        seller_external: isDemandPatti
          ? enquiries?.map(enquiry => ({
              ...enquiry.seller_external,
              enquiry_id: enquiry.id,
            }))
          : undefined,

        buyer: isDemandPatti
          ? demandDetail?.demand_by_pk?.buyer?.user[0]
            ? {
                ...demandDetail?.demand_by_pk?.buyer?.user[0],
                shared_id: demandDetail?.demand_by_pk?.buyer?.id,
              }
            : undefined
          : enquiries?.map(enquiry => ({
              ...enquiry.buyer?.user?.[0],
              shared_id: enquiry.buyer?.id,
              enquiry_id: enquiry.id,
            })),

        facilitator: isDemandPatti
          ? {
              ...demandDetail?.demand_by_pk?.facilitator?.facilitator?.[0],
              shared_id: demandDetail?.demand_by_pk?.facilitator?.id,
            }
          : isOfferPatti
          ? {
              ...offerDetail?.offers_by_pk?.facilitator?.facilitator?.[0],
              shared_id: offerDetail?.offers_by_pk?.facilitator?.id,
            }
          : undefined,
        group:
          state?.group?.length && state?.id === patti?.id
            ? state.group
            : isDemandPatti
            ? demandDetail?.demand_by_pk?.demand_seller_groups?.map(gr => {
                return {
                  name: gr?.seller_groups?.name,
                  id: gr?.seller_groups?.id,
                  shared_id: gr?.seller_groups?.id,
                }
              })
            : offerDetail?.offers_by_pk?.offers_buyer_groups?.map(gr => {
                return {
                  name: gr?.buyer_groups?.name,
                  id: gr?.buyer_groups?.id,
                  shared_id: gr?.buyer_groups?.id,
                }
              }),
        buyer_external: isOfferPatti
          ? enquiries?.map(enquiry => ({
              ...enquiry.buyer_external,
              enquiry_id: enquiry.id,
            }))
          : undefined,
        start_date_time: patti?.start_date_time || dayjs(),
        end_date_time: patti?.end_date_time || dayjs().add(1, "day"),
      }
      setState(prev => {
        const finalValues = {
          ...newValues,
          quantity_unit:
            prev.quantity_unit?.shared_id &&
            prev.quantity_unit?.codename === UnitSI?.si_unit?.[0]?.codename
              ? prev.quantity_unit
              : newValues.quantity_unit,
        }
        runComputations(finalValues)
        return finalValues
      })
    } else {
      setState(prev => ({
        ...prev,
        start_date_time: patti?.start_date_time || dayjs(),
        end_date_time: patti?.end_date_time || dayjs().add(1, "day"),
      }))
    }
  }, [
    demandDetail,
    demandEnquiries,
    gradeDetail,
    idNum,
    offerDetail,
    offerEnquiries,
  ])

  function inputProps(name: string, sharedEntity?: SharedEntity) {
    return {
      value: get(state, name, ""),
      error: get(form.errors, name, "") as any,
      onChange: async (e: any) => {
        const stateCopy = cloneDeep(state)
        const value = e?.target ? e.target.value : e

        // Handle Facilitator logic based on the first seller
        const firstSeller = isDemandPatti
          ? name === "buyer"
            ? value
            : stateCopy.buyer
          : Array.isArray(value) && name === "seller"
          ? value[0]
          : name === "seller"
          ? value
          : stateCopy.seller
        let facilitator = firstSeller?.companies?.[0]?.company

        if (name === "mode" && value === "facilitator" && facilitator) {
          const { data: facilitatorShared } = await upsertSharedEntity({
            reference_id: facilitator.codename,
            group_name: SharedEntityGroup.KiteCompany,
            name: facilitator.name,
          })
          let shared_id =
            facilitatorShared?.insert_setu_trade_shared_entity_one?.id
          set(stateCopy, "facilitator", { ...facilitator, shared_id })
        } else if (
          (name === "mode" && value === "direct") ||
          (name === "mode" && value === "facilitator" && !facilitator) ||
          (name === "seller" &&
            (!firstSeller || !firstSeller?.companies?.length))
        ) {
          set(stateCopy, "facilitator", null)
          set(stateCopy, "mode", "direct")
        }

        if (sharedEntity) {
          if (Array.isArray(value)) {
            const newValue = await Promise.all(
              value.map(async (item: any) => {
                if (item?.codename && item?.name && !item?.shared_id) {
                  const { data } = await upsertSharedEntity({
                    reference_id: item.codename,
                    group_name: sharedEntity.group,
                    name: item.name,
                  })
                  return {
                    ...item,
                    shared_id: data?.insert_setu_trade_shared_entity_one?.id,
                  }
                }
                return item
              })
            )
            set(stateCopy, name, newValue)
          } else if (value?.codename && value?.name && !value?.shared_id) {
            const { data } = await upsertSharedEntity({
              reference_id: value.codename,
              group_name: sharedEntity.group,
              name: value.name,
            })
            set(stateCopy, name, {
              ...value,
              shared_id: data?.insert_setu_trade_shared_entity_one?.id,
            })
          } else {
            set(stateCopy, name, value)
          }
        } else {
          set(stateCopy, name, value)
        }
        if (name === "seller_source_kind") {
          set(stateCopy, "seller_file", null)
        }
        if (name === "buyer_source_kind") {
          set(stateCopy, "buyer_file", null)
        }

        runComputations(stateCopy)
        setState(stateCopy)
      },
    }
  }
  const finalTotal =
    (form.values.total_amount || 0) - (form.values.amount_deducted || 0)

  const onSubmit = async () => {
    const validation = form.validate()
    if (validation.hasErrors) {
      return toast.custom(
        <Toast
          title="Please fix validation errors"
          intent="danger"
          subtitle={Object.values(validation.errors)?.[0]}
        />
      )
    }

    if (!state.quantity_unit?.id) {
      return toast.custom(<Toast title={"Unit is required"} intent="danger" />)
    }
    if (dayjs(state?.end_date_time).isBefore(dayjs(state?.start_date_time))) {
      return toast.custom(
        <Toast
          title={"Start date time should be before end date time"}
          intent="danger"
        />
      )
    }

    if (
      Number(state.trade_items?.[0]?.max_rate) &&
      Number(state.trade_items?.[0]?.max_rate) <
        Number(state.trade_items?.[0]?.rate)
    ) {
      return toast.custom(
        <Toast
          title={"Max rate should be greater than min rate"}
          intent="danger"
        />
      )
    }
    const { data: deptData } = await upsertSharedEntity({
      group_name: SharedEntityGroup.KiteDepartment,
      name: currentDept?.name,
      reference_id: currentDept?.id,
    })

    if (!isEmpty(removedExpenseItems))
      await deleteExpenseItem({
        ids: Object.keys(removedExpenseItems),
      })
    const quantityUnit = state?.quantity_unit

    const updateObj = {
      id: id === "new" ? undefined : state?.id,
      mode: state?.mode,
      material_kind: state?.material_kind,
      buyer_tnc: state?.buyer_tnc,
      seller_tnc: state?.seller_tnc,
      // material_kind: "commodity",
      department_id: deptData?.insert_setu_trade_shared_entity_one?.id,
      start_date_time: state?.start_date_time,
      end_date_time: state?.end_date_time,
      commodity_id: state?.commodity?.shared_id,
      commodity_variety_id: state?.commodity_variety?.shared_id,
      seller_id: isOfferPatti ? state?.seller?.shared_id : undefined,
      buyer_id: isDemandPatti ? state?.buyer?.shared_id : undefined,
      standard_deduction: state?.standard_deduction
        ? Number(state?.standard_deduction)
        : undefined,
      expenses: state?.expenses?.filter(
        i => i.seller || i.buyer || i.facilitator
      )?.length
        ? state?.expenses?.reduce((prev, expense) => {
            const commonProperties = {
              expense_parameter_id: expense.expense_parameter?.id,
              kind: expense.kind?.id,
            }
            if (expense.buyer?.value) {
              prev.push({
                ...commonProperties,
                ...(id !== "new" && {
                  id: expense.buyer.id,
                }),
                paid_by: "buyer",
                value: expense.buyer.value,
              })
            }
            if (expense.seller?.value) {
              prev.push({
                ...commonProperties,
                ...(id !== "new" && {
                  id: expense.seller.id,
                }),
                paid_by: "seller",
                value: expense.seller.value,
              })
            }
            if (expense.facilitator?.value) {
              prev.push({
                ...commonProperties,
                ...(id !== "new" && {
                  id: expense.facilitator.id,
                }),
                paid_by: "facilitator",
                value: expense.facilitator.value,
              })
            }
            return prev
          }, [])
        : undefined,

      sr_officer_id: state?.officer?.shared_id,
      jr_officer_id: state?.junior_officer?.shared_id,
      rate: Number(state?.trade_items?.[0]?.rate) || 0,
      max_rate: Number(state?.trade_items?.[0]?.max_rate) || undefined,
      quantity: Number(state?.trade_items?.[0]?.quantity) || 0,
      rate_unit_id: state?.quantity_unit?.shared_id,
      quantity_unit_id: state?.quantity_unit?.shared_id,
      seller_source_kind: isDemandPatti ? state?.seller_source_kind : undefined,
      buyer_source_kind: isOfferPatti ? state?.buyer_source_kind : undefined,
      variety_grade_id: state?.trade_items?.[0]?.variety_grade?.shared_id,
      facilitator_id: state?.facilitator?.shared_id,
      packaging: state?.packaging,
      buyer_file_id: state?.buyer_file?.media?.id,
      seller_file_id: state?.seller_file?.media?.id,
      seller_group_ids: isDemandPatti
        ? state?.group?.map(a => {
            return a.shared_id
          })
        : undefined,
      buyer_group_ids: isOfferPatti
        ? state?.group?.map(a => {
            return a.shared_id
          })
        : undefined,
      attachments: form.values.attachments?.map(a => {
        return {
          id: a.id,
          media_id: a?.media?.id,
          send_whatsapp: a?.send_whatsapp,
        }
      }),
      enquiries: isDemandPatti
        ? state.seller_source_kind === "external"
          ? state?.seller_external?.map(seller => ({
              id: seller?.enquiry_id,
              seller_external_id: seller.id,
            }))
          : state?.seller?.map(seller => ({
              id: seller?.enquiry_id,
              seller_id: seller.shared_id,
            }))
        : isOfferPatti
        ? state.buyer_source_kind === "external"
          ? state?.buyer_external?.map(seller => ({
              id: seller?.enquiry_id,
              buyer_external_id: seller.id,
            }))
          : state?.buyer?.map(seller => ({
              id: seller?.enquiry_id,
              buyer_id: seller.shared_id,
            }))
        : undefined,
    }

    if (isDemandPatti) {
      const { data: iData, error } = await upsertDemand({
        input: updateObj,
      })
      if (error || !iData) {
        return toast.custom(
          <Toast title={"Unable to create the trade request"} intent="danger" />
        )
      }
    } else {
      const { data: iData, error } = await upsertOffer({
        input: updateObj,
      })
      if (error || !iData) {
        return toast.custom(
          <Toast title={"Unable to create the trade request"} intent="danger" />
        )
      }
    }
    navigate(`${AppRoutes.stockManagement}/${type}`)
    toast.custom(<Toast title={"Patti created successfully!"} />)
  }

  const isCompletelyDisabled =
    (Boolean(idNum) && state?.status !== "created") ||
    (isOfferPatti && maxQuantity === 0)

  // Auto select grade for packaging material type
  const [{ data: packagingGrades }] = useAllVarietyGradesQuery({
    variables: { variety: state?.commodity_variety?.id },
    pause: !isPackaging || !state?.commodity_variety?.id,
  })

  // Auto select grade for packaging material type
  useEffect(() => {
    async function initGrade() {
      if (isPackaging && packagingGrades?.variety_grade?.length) {
        let firstGrade = packagingGrades.variety_grade[0]

        const needsUpdate = state?.trade_items?.some(
          (item: any) =>
            !item.variety_grade ||
            (item.variety_grade.id !== firstGrade.id &&
              !packagingGrades.variety_grade.some(
                g => g.id === item.variety_grade?.id
              ))
        )

        if (needsUpdate) {
          let selection = { ...firstGrade } as any
          if (!selection.shared_id) {
            if (fetchingGradeRef.current.has(firstGrade.id)) return
            fetchingGradeRef.current.add(firstGrade.id)

            try {
              const { data } = await upsertSharedEntity({
                reference_id: selection.codename,
                group_name: SharedEntityGroup.KiteGrade,
                name: selection.name,
              })
              selection.shared_id =
                data?.insert_setu_trade_shared_entity_one?.id
            } finally {
              fetchingGradeRef.current.delete(firstGrade.id)
            }
          }

          setState((prev: any) => {
            if (!prev?.trade_items) return prev
            let changed = false
            const newItems = cloneDeep(prev.trade_items)
            newItems.forEach((item: any) => {
              if (
                !item.variety_grade ||
                (item.variety_grade.id !== firstGrade.id &&
                  !packagingGrades.variety_grade.some(
                    g => g.id === item.variety_grade?.id
                  ))
              ) {
                item.variety_grade = selection
                changed = true
              }
            })
            if (changed) {
              const newState = { ...prev, trade_items: newItems }
              setTimeout(() => runComputations(newState), 0)
              return newState
            }
            return prev
          })
        }
      }
    }
    initGrade()
  }, [
    packagingGrades,
    isPackaging,
    runComputations,
    state?.trade_items?.length,
  ])

  return (
    <>
      <header className="bg-gray-100 shadow-sm p-3 border-b-gray-300 border-b flex justify-between">
        <Breadcrumbs
          items={[
            {
              text: "Trade Requests",
              link: "/d/trade-requests",
              isActive: false,
            },
            {
              text: id?.toString() || "New",
              link: `/d/trade-requests/${id}`,
              isActive: true,
            },
          ]}
          linkRenderer={(link, item) => {
            return <Link to={link as any}>{item}</Link>
          }}
        />
        <Link to={`?timeline=${id}`}>Timeline</Link>
      </header>
      <Group className="flex flex-col md:flex-row gap-4 px-2 md:px-5 pt-8 pb-16 relative">
        <SpinnerOverlay
          show={
            demandFetching ||
            demandEnquiriesFetching ||
            offerFetching ||
            offerEnquiriesFetching ||
            gradeDetailFetching ||
            remainingQuantityFetching ||
            storagePattisFetching
          }
        />
        <SideBar
          key={state?.id || `trades_${state?.trades?.length}`}
          kind={state?.kind}
          storagePattis={storagePattis?.storage_dashboard?.[0]?.pattis}
        />
        <div className="w-full md:w-[70%] mx-auto">
          {isOfferPatti && maxQuantity === 0 && (
            <div className="pb-3 w-full">
              <Toast
                className="!min-w-full opacity-70"
                title="Cannot create Offer. No Stock available!"
                intent="danger"
              />
            </div>
          )}
          <WhoIsTrading
            form={form}
            inputProps={inputProps}
            showBuyer={state?.kind !== TradeKinds.StoragePatti}
            multipleSeller={isDemandPatti}
            multipleTrader={isOfferPatti}
            isDisabled={isCompletelyDisabled}
            uploadSeller={isDemandPatti}
            uploadBuyer={isOfferPatti}
          />
          <HowIsItTraded
            form={form}
            type={isDemandPatti ? "Demand Patti" : "Offer Patti"}
            inputProps={inputProps}
            showBookingKinds
            useBuyerFacilitatorCompanies={isDemandPatti}
            isDisabled={isCompletelyDisabled}
          />
          <WhoOverseesTrading
            inputProps={inputProps}
            isDisabled={isCompletelyDisabled}
          />
          {isPackaging ? null : (
            <WhatIsBeingTraded
              form={form}
              inputProps={inputProps}
              isDisabled={isCompletelyDisabled}
              disableCommodity={Boolean(commodityId)}
              disablePlotCrop={true}
              disableVariety={Boolean(varietyId)}
            />
          )}
          <FormCard title="When is it traded">
            <div
              className={"grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4"}
            >
              <FormDateInput
                required
                label="Start Date of Trade"
                {...inputProps("start_date_time")}
                disabled={isCompletelyDisabled}
              />
              <FormTimeInput
                label="Start Time"
                {...inputProps("start_date_time")}
                defaultValue={dayjs().format("HH:mm")}
                disabled={isCompletelyDisabled}
              />
              <FormDateInput
                label="End Date of Trade"
                {...inputProps("end_date_time")}
                error={
                  dayjs(state?.end_date_time).isBefore(
                    dayjs(state?.start_date_time)
                  )
                    ? "End date time should be after start date time"
                    : ""
                }
                disabled={isCompletelyDisabled}
              />

              <FormTimeInput
                label="End Time"
                {...inputProps("end_date_time")}
                error={
                  dayjs(state?.end_date_time).isBefore(
                    dayjs(state?.start_date_time)
                  )
                    ? "End date time should be after start date time"
                    : ""
                }
                disabled={isCompletelyDisabled}
              />
            </div>
            {state?.seller_file || state?.buyer_file ? (
              <Toast
                intent="info"
                variant="minimal"
                className="max-w-full mt-2 text-sm"
                title={
                  "The Offer will go live only after the user data is successfully uploaded to the system. This process may take some time, so please wait until the upload is complete."
                }
              ></Toast>
            ) : null}
          </FormCard>

          {isPackaging ? (
            <FormCard title="Trade Particulars">
              <div className="mb-3 border rounded-lg overflow-hidden">
                <header className="flex flex-col md:flex-row md:justify-between md:items-center gap-3 p-3 md:p-4 bg-gradient-to-r from-green-50 to-transparent border-b">
                  <h6 className="text-base font-semibold text-gray-800">
                    Multiple Packaging
                  </h6>

                  <div className="flex flex-wrap gap-2 items-center">
                    <Checkbox
                      checked={state?.is_rate_range}
                      {...inputProps("is_rate_range")}
                      onChange={e => {
                        const stateCopy = cloneDeep(state)
                        stateCopy.is_rate_range = e
                        stateCopy.trade_items[0].max_rate = 0
                        setState(stateCopy)
                        runComputations(stateCopy)
                      }}
                      label="Rate Range"
                      disabled={isCompletelyDisabled}
                    />
                  </div>
                </header>

                <div className="p-3 md:p-4 space-y-4">
                  {state?.trade_items?.map((item, index) => {
                    let itemRemoved = removedTradeItems[item.id] ? true : false
                    return (
                      <div
                        key={index}
                        className={classNames(
                          "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 p-4 rounded-lg border-2 transition-all",
                          itemRemoved
                            ? "bg-gray-50 border-gray-200 opacity-60"
                            : "bg-white border-green-100 hover:border-green-200 hover:shadow-sm"
                        )}
                      >
                        <FormQuerySelector
                          label="Packaging Material Type"
                          serverSideQuery
                          dataHook={useAllPackagingCommoditiesQuery}
                          {...inputProps("commodity", {
                            group: SharedEntityGroup.KiteCommodity,
                          })}
                          className={"w-full"}
                          required
                          isDisabled={
                            isCompletelyDisabled || Boolean(commodityId)
                          }
                        />

                        <FormQuerySelector
                          label="Packaging Name"
                          dataHook={useAllVarietiesQuery}
                          variables={{
                            commodity_id: form.values.commodity?.id,
                          }}
                          {...inputProps("commodity_variety", {
                            group: SharedEntityGroup.KiteVariety,
                          })}
                          className={"w-full"}
                          required
                          isDisabled={
                            isCompletelyDisabled || Boolean(varietyId)
                          }
                        />
                        <div className="flex  w-full">
                          <FormInput
                            {...inputProps(`trade_items.${index}.${"rate"}`)}
                            inputProps={{
                              ...inputProps(`trade_items.${index}.${"rate"}`),
                              name: `trade_items.${index}`,
                              type: "number",
                            }}
                            label="Rate"
                            intent={
                              inputProps(`trade_items.${index}.${"rate"}`)
                                .value &&
                              inputProps(`trade_items.${index}.${"rate"}`)
                                .value <
                                inputProps(`trade_items.${index}.${"rate"}`)
                                  .value
                                ? "danger"
                                : "none"
                            }
                            disabled={isCompletelyDisabled}
                            className="w-full"
                            leftElement={
                              <Tag className="ml-1" variant="minimal">
                                {defaultCurrency?.name || "INR"}
                              </Tag>
                            }
                          />

                          {state?.is_rate_range ? (
                            <FormInput
                              {...inputProps(`trade_items.${index}.max_rate`)}
                              inputProps={{
                                ...inputProps(`trade_items.${index}.max_rate`),
                                name: `trade_items.${index}`,
                                type: "number",
                              }}
                              label={
                                <div className="!h-full text-white">{"-"}</div>
                              }
                              error={
                                Number(state.trade_items?.[index]?.max_rate) &&
                                Number(state.trade_items?.[index]?.max_rate) <
                                  Number(state.trade_items?.[index]?.rate)
                                  ? "Max rate should be higher than min rate"
                                  : ""
                              }
                              className="w-full"
                              disabled={isCompletelyDisabled}
                              // className="self-end w-full"
                              leftElement={
                                <Tag className="ml-1" variant="minimal">
                                  {defaultCurrency?.name || "INR"}
                                </Tag>
                              }
                            />
                          ) : undefined}
                        </div>

                        <div className="flex">
                          <FormInput
                            label={
                              <div>
                                Quantity{" "}
                                <span className="text-xs">
                                  {isDemandPatti ? "" : `(max: ${maxQuantity})`}
                                </span>
                              </div>
                            }
                            {...inputProps(`trade_items.${index}.quantity`)}
                            className="vt-weight-listner !border-0"
                            inputProps={{
                              ...inputProps(`trade_items.${index}.quantity`),
                              id: "vt_weight_listener",
                              type: "number",
                              onChange: e => {
                                let value = Number(e.target.value)
                                let original = Number(maxQuantity)
                                let result =
                                  !isDemandPatti && value > original
                                    ? original
                                    : e.target.value
                                inputProps(
                                  `trade_items.${index}.quantity`
                                ).onChange(result)
                              },
                            }}
                            max={isDemandPatti ? undefined : maxQuantity}
                            disabled={isCompletelyDisabled}
                            rightElement={
                              isPackaging ? (
                                <span className="text-sm text-brown-300 mr-1">
                                  Units
                                </span>
                              ) : undefined
                            }
                          />
                          {isPackaging ? null : (
                            <FormQuerySelector
                              label={<div>Unit</div>}
                              components={{
                                IndicatorSeparator: () => null,
                                ClearIndicator: () => null,
                              }}
                              dataHook={useAllSiUnitsQuery}
                              variables={{ type: ["Weight", "SKU"] }}
                              isDisabled={
                                isCompletelyDisabled || itemRemoved || index > 0
                              }
                              {...inputProps(`quantity_unit`, {
                                group: SharedEntityGroup.KiteSiUnit,
                              })}
                              className="!w-[60%] !border-l-0"
                            />
                          )}
                        </div>

                        <div className="flex gap-4">
                          <div className="flex flex-col gap-3">
                            <FormInput
                              label="Total Amount"
                              className="flex-1 text-right"
                              value={inr(
                                (item.quantity || 0) *
                                  (item["max_rate"] || item["rate"] || 0)
                              )}
                              disabled
                            />
                          </div>
                          <div className="flex self-start justify-center">
                            {
                              <button
                                className="text-red-500 pt-5"
                                onClick={async () => {
                                  if (item.id) {
                                    return
                                    // await deleteTradeItem({ id: item.id })
                                  }

                                  const stateCopy = cloneDeep(state)
                                  stateCopy.trade_items.splice(index, 1)
                                  stateCopy.trade_items = stateCopy.trade_items
                                    ?.length
                                    ? stateCopy.trade_items
                                    : [{}]
                                  setState(stateCopy)
                                  runComputations(stateCopy)
                                }}
                                disabled={isCompletelyDisabled}
                              >
                                <Trash height={24} width={24} />
                              </button>
                            }
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
                <div className="border-t-2 border-green-100 bg-gradient-to-b from-gray-50 to-white">
                  <Group className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4 p-3 md:p-4 border-b">
                    <Text className="font-semibold text-gray-700 md:flex-[2_1_0%]">
                      Total
                    </Text>
                    <div className="flex justify-between md:block md:flex-1">
                      <span className="text-sm text-gray-500 md:hidden">
                        Quantity:
                      </span>
                      <Text className="text-right font-medium">
                        {state.total_quantity}{" "}
                        {state?.quantity_unit?.name || "na"}
                      </Text>
                    </div>
                    <div className="flex justify-between md:block md:flex-[0.75]">
                      <span className="text-sm text-gray-500 md:hidden">
                        Amount:
                      </span>
                      <Text className="text-right font-medium">
                        {inr(state.total_amount)}
                      </Text>
                    </div>
                    <span className="hidden md:block w-10" />
                  </Group>

                  <Group className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4 p-3 md:p-4 bg-gradient-to-r from-green-100 to-green-50 border-t-2 border-green-200">
                    <Text className="text-lg font-bold text-green-800 md:flex-[2_1_0%]">
                      Final
                    </Text>
                    <div className="flex justify-between md:block md:flex-1">
                      <span className="text-sm font-medium text-green-700 md:hidden">
                        Quantity:
                      </span>
                      <Text className="text-right font-bold text-green-800">
                        {(
                          (form.values.total_quantity || 0) -
                          (form.values.quantity_deducted || 0)
                        )?.toFixed(2)}{" "}
                        {state?.quantity_unit?.name || "na"}
                      </Text>
                    </div>
                    <div className="flex justify-between md:block md:flex-[0.75]">
                      <span className="text-sm font-medium text-green-700 md:hidden">
                        Amount:
                      </span>
                      <Text className="text-right font-bold text-green-800 text-lg">
                        {inr(finalTotal)}
                      </Text>
                    </div>
                    <span className="hidden md:block w-10" />
                  </Group>
                </div>
              </div>
              {/* {!canCreateTrade && creditForm.values?.credit?.id && (
                      <InsufficientCreditNote />
                    )} */}
            </FormCard>
          ) : (
            <FormCard title="Trade Particulars">
              <div className="mb-3 border rounded-lg overflow-hidden">
                <header className="flex flex-col md:flex-row md:justify-between md:items-center gap-3 p-3 md:p-4 bg-gradient-to-r from-green-50 to-transparent border-b">
                  <h6 className="text-base font-semibold text-gray-800">
                    Grade
                  </h6>
                  <Checkbox
                    checked={state?.is_rate_range}
                    {...inputProps("is_rate_range")}
                    onChange={e => {
                      const stateCopy = cloneDeep(state)
                      stateCopy.is_rate_range = e
                      stateCopy.trade_items[0].max_rate = 0
                      setState(stateCopy)
                      runComputations(stateCopy)
                    }}
                    label="Rate Range"
                    disabled={isCompletelyDisabled}
                  />
                </header>

                <div className="p-3 md:p-4 space-y-4">
                  {state?.trade_items?.map((item, index) => {
                    let itemRemoved = removedTradeItems[item.id] ? true : false

                    const baseRateKey = "rate"
                    return (
                      <div
                        key={index}
                        className={classNames(
                          "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 p-4 rounded-lg border-2 transition-all",
                          itemRemoved
                            ? "bg-gray-50 border-gray-200 opacity-60"
                            : "bg-white border-green-100 hover:border-green-200 hover:shadow-sm"
                        )}
                      >
                        <div className="flex-1">
                          <FormQuerySelector
                            label="Grade"
                            dataHook={useAllVarietyGradesQuery}
                            variables={{
                              variety: state?.commodity_variety?.id,
                            }}
                            {...inputProps(
                              `trade_items.${index}.variety_grade`,
                              {
                                group: SharedEntityGroup.KiteGrade,
                              }
                            )}
                            isDisabled={
                              isCompletelyDisabled || Boolean(gradeId)
                            }
                            isOptionDisabled={(opt: any) =>
                              state?.trade_items?.find(
                                item => item.variety_grade?.id === opt.id
                              )
                            }
                          />
                        </div>
                        <div className="flex  w-full">
                          <FormInput
                            {...inputProps(
                              `trade_items.${index}.${baseRateKey}`
                            )}
                            inputProps={{
                              ...inputProps(
                                `trade_items.${index}.${baseRateKey}`
                              ),
                              name: `trade_items.${index}`,
                              type: "number",
                            }}
                            label="Rate"
                            intent={
                              inputProps(`trade_items.${index}.${baseRateKey}`)
                                .value &&
                              inputProps(`trade_items.${index}.${baseRateKey}`)
                                .value <
                                inputProps(
                                  `trade_items.${index}.${baseRateKey}`
                                ).value
                                ? "danger"
                                : "none"
                            }
                            disabled={isCompletelyDisabled}
                            className="w-full"
                            leftElement={
                              <Tag className="ml-1" variant="minimal">
                                {defaultCurrency?.name || "INR"}
                              </Tag>
                            }
                          />

                          {state?.is_rate_range ? (
                            <FormInput
                              {...inputProps(`trade_items.${index}.max_rate`)}
                              inputProps={{
                                ...inputProps(`trade_items.${index}.max_rate`),
                                name: `trade_items.${index}`,
                                type: "number",
                              }}
                              label={
                                <div className="!h-full text-white">{"-"}</div>
                              }
                              error={
                                Number(state.trade_items?.[index]?.max_rate) &&
                                Number(state.trade_items?.[index]?.max_rate) <
                                  Number(state.trade_items?.[index]?.rate)
                                  ? "Max rate should be higher than min rate"
                                  : ""
                              }
                              className="w-full"
                              disabled={isCompletelyDisabled}
                              // className="self-end w-full"
                              leftElement={
                                <Tag className="ml-1" variant="minimal">
                                  {defaultCurrency?.name || "INR"}
                                </Tag>
                              }
                            />
                          ) : undefined}
                        </div>

                        <div className="flex">
                          <FormInput
                            label={
                              <div>
                                Quantity{" "}
                                <span className="text-xs">
                                  {isDemandPatti ? "" : `(max: ${maxQuantity})`}
                                </span>
                              </div>
                            }
                            {...inputProps(`trade_items.${index}.quantity`)}
                            className="vt-weight-listner !border-0"
                            inputProps={{
                              ...inputProps(`trade_items.${index}.quantity`),
                              id: "vt_weight_listener",
                              type: "number",
                              onChange: e => {
                                let value = Number(e.target.value)
                                let original = Number(maxQuantity)
                                let result =
                                  !isDemandPatti && value > original
                                    ? original
                                    : e.target.value
                                inputProps(
                                  `trade_items.${index}.quantity`
                                ).onChange(result)
                              },
                            }}
                            max={isDemandPatti ? undefined : maxQuantity}
                            disabled={isCompletelyDisabled}
                          />
                          <FormQuerySelector
                            label={<div>Unit</div>}
                            components={{
                              IndicatorSeparator: () => null,
                              ClearIndicator: () => null,
                            }}
                            dataHook={useAllSiUnitsQuery}
                            variables={{ type: ["Weight", "SKU"] }}
                            isDisabled={
                              isCompletelyDisabled || itemRemoved || index > 0
                            }
                            {...inputProps(`quantity_unit`, {
                              group: SharedEntityGroup.KiteSiUnit,
                            })}
                            className="!w-[60%] !border-l-0"
                          />
                        </div>

                        <div className="flex gap-4">
                          <div className="flex flex-col gap-3">
                            <FormInput
                              label="Total Amount"
                              className="flex-1 text-right"
                              value={inr(
                                (item.quantity || 0) *
                                  (item["max_rate"] || item[baseRateKey] || 0)
                              )}
                              disabled
                            />
                          </div>
                          <div className="flex self-start justify-center">
                            {
                              <button
                                className="text-red-500 pt-5"
                                onClick={async () => {
                                  if (item.id) {
                                    return
                                    // await deleteTradeItem({ id: item.id })
                                  }

                                  const stateCopy = cloneDeep(state)
                                  stateCopy.trade_items.splice(index, 1)
                                  stateCopy.trade_items = stateCopy.trade_items
                                    ?.length
                                    ? stateCopy.trade_items
                                    : [{}]
                                  setState(stateCopy)
                                  runComputations(stateCopy)
                                }}
                                disabled={isCompletelyDisabled}
                              >
                                <Trash height={24} width={24} />
                              </button>
                            }
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
                <div className="border-t-2 border-green-100 bg-gradient-to-b from-gray-50 to-white">
                  <Group className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4 p-3 md:p-4 border-b">
                    <Text className="font-semibold text-gray-700 md:flex-[2_1_0%]">
                      Total
                    </Text>
                    <div className="flex justify-between md:block md:flex-1">
                      <span className="text-sm text-gray-500 md:hidden">
                        Quantity:
                      </span>
                      <Text className="text-right font-medium">
                        {state.total_quantity}{" "}
                        {state?.quantity_unit?.name || "na"}
                      </Text>
                    </div>
                    <div className="flex justify-between md:block md:flex-[0.75]">
                      <span className="text-sm text-gray-500 md:hidden">
                        Amount:
                      </span>
                      <Text className="text-right font-medium">
                        {inr(state.total_amount)}
                      </Text>
                    </div>
                    <span className="hidden md:block w-10" />
                  </Group>
                  <Group className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4 p-3 md:p-4 border-b bg-blue-50/30">
                    <Text className="font-semibold text-gray-700 md:flex-1">
                      Standard Deduction
                    </Text>
                    <Group className="flex gap-2 items-center md:flex-1">
                      <InputGroup
                        inputProps={{
                          ...inputProps("standard_deduction"),
                          max: 100,
                          min: 0,
                          type: "number",
                          placeholder: "Value in %",
                          className: "text-right",
                        }}
                        className="flex-1"
                        disabled={isCompletelyDisabled}
                        rightElement={<span className="mr-2">%</span>}
                      />
                    </Group>
                    <div className="flex justify-between md:block md:flex-1">
                      <span className="text-sm text-gray-500 md:hidden">
                        Quantity:
                      </span>
                      <Text className="text-right">
                        {state.quantity_deducted?.toFixed?.(2)}{" "}
                        {state?.quantity_unit?.name || "na"}
                      </Text>
                    </div>
                    <div className="flex justify-between md:block md:flex-[0.75]">
                      <span className="text-sm text-gray-500 md:hidden">
                        Amount:
                      </span>
                      <Text className="text-right">
                        {inr(state.amount_deducted)}
                      </Text>
                    </div>
                    <span className="hidden md:block w-10" />
                  </Group>
                  <Group className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4 p-3 md:p-4 bg-gradient-to-r from-green-100 to-green-50 border-t-2 border-green-200">
                    <Text className="text-lg font-bold text-green-800 md:flex-[2_1_0%]">
                      Final
                    </Text>
                    <div className="flex justify-between md:block md:flex-1">
                      <span className="text-sm font-medium text-green-700 md:hidden">
                        Quantity:
                      </span>
                      <Text className="text-right font-bold text-green-800">
                        {(
                          (form.values.total_quantity || 0) -
                          (form.values.quantity_deducted || 0)
                        )?.toFixed(2)}{" "}
                        {state?.quantity_unit?.name || "na"}
                      </Text>
                    </div>
                    <div className="flex justify-between md:block md:flex-[0.75]">
                      <span className="text-sm font-medium text-green-700 md:hidden">
                        Amount:
                      </span>
                      <Text className="text-right font-bold text-green-800 text-lg">
                        {inr(finalTotal)}
                      </Text>
                    </div>
                    <span className="hidden md:block w-10" />
                  </Group>
                </div>
              </div>
            </FormCard>
          )}
          <TradeExpenses
            form={form}
            inputProps={inputProps}
            advancePaymentDisabled={true}
            creditNoteDisabled={true}
            isDisabled={isCompletelyDisabled}
            sharedDeptId={sharedDeptId}
            isStatusGenerateSale={false}
            onExpenseTemplateChange={expenseConfig => {
              const stateCopy = cloneDeep(form.values)
              for (const expense of stateCopy.expenses) {
                setRemovedExpenseItems(prev => {
                  if (expense?.buyer?.id) {
                    prev[expense.buyer.id] = true
                  }
                  if (expense?.seller?.id) {
                    prev[expense.seller.id] = true
                  }
                  return prev
                })
              }
              stateCopy.expenses = normalizeExpenses(
                expenseConfig.expense_configs || [],
                true
              )
              runComputations(stateCopy)
              form.setValues(stateCopy)
            }}
            onDeleteExpense={(idx, expense) => {
              const stateCopy = cloneDeep(form.values)
              stateCopy.expenses.splice(idx, 1)

              setRemovedExpenseItems(prev => {
                if (expense?.buyer?.id) {
                  prev[expense.buyer.id] = true
                }
                if (expense?.seller?.id) {
                  prev[expense.seller.id] = true
                }
                if (expense?.facilitator?.id) {
                  prev[expense.facilitator.id] = true
                }
                return prev
              })
              form.setValues(stateCopy)
              runComputations(stateCopy)
            }}
            onDeleteAdvancePayment={idx => {
              const stateCopy = cloneDeep(form.values)
              stateCopy.advance_payment.splice(idx, 1)
              form.setValues(stateCopy)
              runComputations(stateCopy)
            }}
          />
          <Attachments
            attachments={form.values?.attachments || []}
            onChange={attachments =>
              form.setFieldValue("attachments", attachments)
            }
            disabled={isCompletelyDisabled}
          />
          <TermsAndConditions
            isDisabled={isCompletelyDisabled}
            kind={form.values.kind}
            mode={form.values.mode}
            sharedDeptId={sharedDeptId}
            seller_tnc={form.values.seller_tnc}
            buyer_tnc={form.values.buyer_tnc}
            onChange={(path, value) => form.setFieldValue(path, value)}
          />
        </div>
      </Group>
      <footer className="fixed bg-white left-0 bottom-0 w-full p-2 shadow-sm flex justify-end gap-4">
        <Button
          loading={mutationFetching}
          onClick={async () => {
            await onSubmit()
          }}
          leftIcon={<CheckCircle />}
          disabled={isCompletelyDisabled}
        >
          {state?.id ? "Save" : "Create"}{" "}
          {isDemandPatti ? "Demand Patti" : "Offer Patti"}
        </Button>
      </footer>

      {attachmentModal && (
        <AttachmentModal
          form={form}
          isOpen={attachmentModal}
          attachments={form.values?.attachments || []}
          addAttachment={attachment => {
            form.setFieldValue("attachments", attachment)
          }}
          disabled={false}
          onClose={() => {
            setAttachmentModal(false)
          }}
        />
      )}
    </>
  )
}

export default PattiDetail
